/**
 * LinkedAgentTaskPicker — Cloud GM Company Agent (Feb 2026 / iter-200).
 *
 * Replaces LinkedTaxInvoicePicker per user request:
 *   "شيل الفاتورة الضريبية — استبدلها بطلب الوكيل (AGNT-XXXXXX)."
 *
 * Given the currently-entered `agentEmail`, queries
 *   GET /api/admin/assigned-tasks?target_type=agent&q=<email>
 * and lets the admin pick ONE assigned task (contract) to link to the
 * company file. Picking populates `linked_agent_task_*` on the form; the
 * "unlink" chip clears them.
 *
 * Props:
 *   - agentEmail:           string   — the current agent's email (may be empty)
 *   - selectedId:           string   — currently-linked task id (from form)
 *   - selectedRef:          string   — currently-linked reference (AGNT-XXXXXX)
 *   - selectedSnapshot:     object   — current snapshot on the form
 *   - onSelect(task|null):  callback — fired with the picked task or null
 */
import React, { useEffect, useState, useRef } from 'react';
import axios from 'axios';
import { motion } from 'framer-motion';
import { FileText, Loader2, X, Check, Info } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';
import { getAuthHeaders } from '../../utils/authHeaders';
import { isValidEmail } from '../../utils/validators';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const LinkedAgentTaskPicker = ({
  agentEmail,
  selectedId,
  selectedRef,
  selectedSnapshot,
  onSelect,
}) => {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(false);
  const debounce = useRef(null);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    const email = (agentEmail || '').trim();
    if (!email || !isValidEmail(email)) {
      setTasks([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/admin/assigned-tasks`, {
          headers: getAuthHeaders(),
          params: {
            target_type: 'agent',
            q: email,
            limit: 50,
          },
        });
        // Filter to tasks whose target_email actually matches — the `q` param
        // does a broad regex search across multiple fields, so we tighten it
        // here to only tasks assigned to THIS agent email.
        const list = (r.data?.data || []).filter(
          (t) => String(t.target_email || '').trim().toLowerCase() === email.toLowerCase()
        );
        setTasks(list);
      } catch {
        setTasks([]);
      } finally {
        setLoading(false);
      }
    }, 400);
    return () => { if (debounce.current) clearTimeout(debounce.current); };
  }, [agentEmail]);

  const hasEmail = !!(agentEmail || '').trim() && isValidEmail(agentEmail);

  return (
    <motion.section
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white/[0.02] border border-white/10 rounded-2xl p-6 mb-6"
      data-testid="linked-agent-task-section"
    >
      <div className="flex items-center gap-2 mb-3">
        <FileText className="h-5 w-5 text-amber-400" />
        <h2 className="font-semibold text-white">
          {isAr ? 'طلب الوكيل المرتبط (اختياري)' : 'Linked Agent Task (optional)'}
        </h2>
      </div>

      <p className="text-xs text-[#808080] mb-4 leading-relaxed">
        {isAr
          ? 'اربط ملف الشركة بطلب المهمة الخاص بالوكيل (AGNT-XXXXXX). يتم البحث تلقائياً ببريد الوكيل.'
          : "Link this company file to the agent's assigned task (AGNT-XXXXXX). We search automatically using the agent email."}
      </p>

      {/* Currently-selected chip (if any) */}
      {selectedId && (
        <div
          className="mb-4 flex items-center justify-between gap-2 rounded-xl border border-amber-500/30 bg-amber-500/10 px-3 py-2"
          data-testid="linked-agent-task-selected"
        >
          <div className="flex items-center gap-2 min-w-0">
            <Check className="h-4 w-4 text-amber-300 shrink-0" />
            <div className="min-w-0">
              <div className="text-sm text-amber-100 truncate">
                {isAr ? 'مربوط بالطلب رقم:' : 'Linked to task:'}{' '}
                <span className="font-semibold text-white font-mono">{selectedRef || selectedId}</span>
              </div>
              {selectedSnapshot && (
                <div className="text-[11px] text-amber-200/80 truncate">
                  {isAr
                    ? (selectedSnapshot.service_name_ar || selectedSnapshot.service_name_en)
                    : (selectedSnapshot.service_name_en || selectedSnapshot.service_name_ar)}
                  {selectedSnapshot.target_name && (<> · {selectedSnapshot.target_name}</>)}
                  {selectedSnapshot.created_at && (<> · {String(selectedSnapshot.created_at).slice(0, 10)}</>)}
                </div>
              )}
            </div>
          </div>
          <button
            onClick={() => onSelect(null)}
            className="p-1.5 rounded-md text-red-300 hover:bg-red-500/10 shrink-0"
            title={isAr ? 'فك الربط' : 'Unlink'}
            data-testid="linked-agent-task-unlink-btn"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* States */}
      {!hasEmail && (
        <div className="flex items-center gap-2 text-xs text-[#808080] bg-[#050505] border border-white/5 rounded-lg px-3 py-2">
          <Info className="h-3.5 w-3.5" />
          {isAr
            ? 'أدخل بريد وكيل الشركة أعلاه لعرض طلباته المسجّلة في المهام اليدوية.'
            : "Enter the company agent's email above to list their assigned tasks."}
        </div>
      )}

      {hasEmail && loading && (
        <div className="flex items-center gap-2 text-xs text-cyan-300 px-3 py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          {isAr ? 'جاري البحث عن الطلبات…' : 'Searching tasks…'}
        </div>
      )}

      {hasEmail && !loading && tasks.length === 0 && (
        <div className="flex items-center gap-2 text-xs text-[#808080] bg-[#050505] border border-white/5 rounded-lg px-3 py-2" data-testid="linked-agent-task-empty">
          <Info className="h-3.5 w-3.5" />
          {isAr
            ? 'لا توجد طلبات مسجّلة لهذا الوكيل بعد.'
            : 'No assigned tasks registered for this agent yet.'}
        </div>
      )}

      {hasEmail && !loading && tasks.length > 0 && (
        <div className="space-y-2 max-h-[420px] overflow-y-auto pr-1" data-testid="linked-agent-task-list">
          {tasks.map((t) => {
            const active = t.id === selectedId;
            const ref = t.request_no_display || `#${t.request_no || t.id.slice(0, 6)}`;
            const svc = isAr
              ? (t.service_name_ar || t.service_name_en || '—')
              : (t.service_name_en || t.service_name_ar || '—');
            const date = t.created_at ? String(t.created_at).slice(0, 10) : '';
            return (
              <button
                type="button"
                key={t.id}
                onClick={() => onSelect(active ? null : t)}
                data-testid={`linked-agent-task-item-${ref}`}
                className={`w-full text-start rounded-xl border px-3 py-2 transition-colors ${
                  active
                    ? 'border-amber-500/50 bg-amber-500/10'
                    : 'border-white/10 bg-[#050505] hover:bg-white/5'
                }`}
              >
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0">
                    <FileText className={`h-4 w-4 shrink-0 ${active ? 'text-amber-300' : 'text-amber-400'}`} />
                    <span className="text-sm text-white font-semibold font-mono">{ref}</span>
                    {t.status && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 text-[#808080]">{t.status}</span>
                    )}
                  </div>
                  {date && (
                    <div className="text-xs text-amber-200/90 font-mono">{date}</div>
                  )}
                </div>
                <div className="text-xs text-[#a0a0a0] mt-1 flex flex-wrap gap-x-3 gap-y-0.5">
                  <span>{isAr ? 'الخدمة:' : 'Service:'} <span className="text-white/80">{svc}</span></span>
                  {t.target_name && <span>{isAr ? 'للوكيل:' : 'Agent:'} <span className="text-white/80">{t.target_name}</span></span>}
                  {t.review_status && (
                    <span className="text-white/60">
                      {isAr ? 'المراجعة:' : 'Review:'} {t.review_status}
                    </span>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      )}
    </motion.section>
  );
};

/** Helper: convert an assigned-task row into the snapshot format we persist. */
export const taskToSnapshot = (task) => {
  if (!task) return null;
  return {
    id: task.id,
    request_no_display: task.request_no_display || '',
    service_name_ar:    task.service_name_ar || '',
    service_name_en:    task.service_name_en || '',
    target_name:        task.target_name || '',
    target_email:       task.target_email || '',
    status:             task.status || '',
    review_status:      task.review_status || '',
    created_at:         task.created_at || '',
  };
};

export default LinkedAgentTaskPicker;
